import React, { useState } from "react";
import { Bar } from "react-chartjs-2";
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  Title, 
  Tooltip, 
  Legend 
} from "chart.js";
import { Box, Typography, CircularProgress, useTheme, alpha, Divider, ButtonBase } from "@mui/material";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// Define the expected data structure to match API response
interface ClientDataItem {
  buyer_name: string;
  total_approved_requested_credit_limit: number;
  buyer_outstanding_limit: number;
}

interface ClientCreditLimitBarChartProps {
  creditLimitData?: { data: ClientDataItem[] };
  isLoading?: boolean;
  error?: any;
}

const ClientCreditLimitBarChart: React.FC<ClientCreditLimitBarChartProps> = ({ 
  creditLimitData,
  isLoading = false,
  error = null
}) => {
  const theme = useTheme();
  
  // State to track which datasets are visible
  const [visibleDatasets, setVisibleDatasets] = useState({
    approved: true,
    outstanding: true
  });
  
  // Enhanced chart colors with gradients
  const totalLimitColor = theme.palette.primary.main;
  const outstandingLimitColor = theme.palette.error.main;
  
  // Toggle dataset visibility
  const toggleDataset = (dataset: 'approved' | 'outstanding') => {
    setVisibleDatasets({
      ...visibleDatasets,
      [dataset]: !visibleDatasets[dataset]
    });
  };
  
  // Transform API data for bar chart
  const createChartData = () => {
    // Check if we have data and the data array
    if (!creditLimitData?.data || creditLimitData.data.length === 0) {
      return null;
    }
    
    // Sort clients by total limit (descending) and get top 10
    const sortedClients = [...creditLimitData.data]
      .sort((a, b) => b.total_approved_requested_credit_limit - a.total_approved_requested_credit_limit)
      .slice(0, 10);
    
    // Create datasets array with only visible datasets
    const datasets = [];
    
    if (visibleDatasets.approved) {
      datasets.push({
        label: 'Approved Credit Limit',
        data: sortedClients.map((client) => client.total_approved_requested_credit_limit),
        backgroundColor: alpha(totalLimitColor, 0.85),
        borderColor: theme.palette.primary.dark,
        borderWidth: 1,
        borderRadius: {
          topLeft: 6,
          topRight: 0,
          bottomLeft: 6,
          bottomRight: 0
        },
        barPercentage: 0.8,
      });
    }
    
    if (visibleDatasets.outstanding) {
      datasets.push({
        label: 'Outstanding Limit',
        data: sortedClients.map((client) => client.buyer_outstanding_limit),
        backgroundColor: alpha(outstandingLimitColor, 0.85),
        borderColor: theme.palette.error.dark,
        borderWidth: 1,
        borderRadius: {
          topLeft: 0,
          topRight: 6,
          bottomLeft: 0,
          bottomRight: 6
        },
        barPercentage: 0.8,
      });
    }
    
    return {
      labels: sortedClients.map((client) => client.buyer_name),
      datasets
    };
  };

  const chartData = createChartData();
  const topClient = creditLimitData?.data && creditLimitData.data.length > 0 ? 
    [...creditLimitData.data].sort((a, b) => b.total_approved_requested_credit_limit - a.total_approved_requested_credit_limit)[0] : null;

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !chartData) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
        <Typography color="error">Failed to load credit limit data</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%" }}>
      {/* Credit legend with clickable indicators */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end', 
        gap: 3, 
        mb: 2,
        px: 1
      }}>
        <ButtonBase 
          onClick={() => toggleDataset('approved')}
          sx={{ 
            opacity: visibleDatasets.approved ? 1 : 0.5,
            transition: 'opacity 0.2s',
            borderRadius: 1,
            padding: '4px 8px',
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: alpha(totalLimitColor, 0.85) }} />
            <Typography variant="caption" color="text.secondary">Approved Limit</Typography>
          </Box>
        </ButtonBase>
        
        <ButtonBase 
          onClick={() => toggleDataset('outstanding')}
          sx={{ 
            opacity: visibleDatasets.outstanding ? 1 : 0.5,
            transition: 'opacity 0.2s',
            borderRadius: 1,
            padding: '4px 8px',
            '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: alpha(outstandingLimitColor, 0.85) }} />
            <Typography variant="caption" color="text.secondary">Outstanding</Typography>
          </Box>
        </ButtonBase>
      </Box>

      <Divider sx={{ mb: 2, opacity: 0.6 }} />
      
      <Box sx={{ height: 'calc(100% - 50px)' }}>
        {/* Main chart */}
        <Bar 
          data={chartData} 
          options={{ 
            maintainAspectRatio: false,
            indexAxis: 'y',
            plugins: {
              legend: {
                display: false // Hide legend since we have custom legend above
              },
              tooltip: {
                backgroundColor: alpha(theme.palette.background.default, 0.95),
                titleColor: theme.palette.text.primary,
                bodyColor: theme.palette.text.secondary,
                titleFont: {
                  size: 13,
                  weight: 'bold'
                },
                bodyFont: {
                  size: 12
                },
                padding: 12,
                borderColor: theme.palette.divider,
                borderWidth: 1,
                boxPadding: 6,
                callbacks: {
                  label: function (context) {
                    return `${context.dataset.label}: $${context.parsed.x.toLocaleString()}`;
                  }
                }
              }
            },
            scales: {
              x: {
                beginAtZero: true,
                stacked: false,
                grid: {
                  color: alpha(theme.palette.divider, 0.1),
                },
                ticks: {
                  padding: 5,
                  callback: function (value) {
                    return `$${(Number(value) / 1000000).toFixed(1)}M`;
                  },
                  font: {
                    size: 10
                  }
                }
              },
              y: {
                grid: {
                  display: false,
                },
                ticks: {
                  font: {
                    size: 11
                  }
                }
              }
            },
            animation: {
              duration: 1200,
              easing: 'easeOutQuart'
            }
          }} 
        />
      </Box>
    </Box>
  );
};

export default ClientCreditLimitBarChart;
